import React, { useState, useEffect, useRef } from 'react';
import { urlFor } from '../sanityClient';
import { useThreadMessages } from '../utils/useThreadMessages';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';

const HeartFilled = () => (
<svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 13 13">
  <path fill="#000" d="M12.188 5.18c0 3.554-5.271 6.432-5.496 6.55a.407.407 0 0 1-.384 0C6.083 11.612.813 8.734.813 5.18A3.152 3.152 0 0 1 3.96 2.03c1.049 0 1.967.451 2.539 1.213.572-.762 1.49-1.213 2.54-1.213a3.152 3.152 0 0 1 3.148 3.149Z"/>
</svg>
);

const Modal = ({ isOpen, onClose, item, userEmail }) => {
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const db = getFirestore();
  const currentUserEmail = userEmail;

  // season pretty
  let seasonPretty = '';
  if (item?.season) {
    const season = item.season.toLowerCase();
    if (season.includes('spring') || season.includes('summer')) {
      seasonPretty = 'SS';
    } else if (season.includes('autumn') || season.includes('winter')) {
      seasonPretty = 'AW';
    } else {
      seasonPretty = item.season;
    }
  }

  let itemTitle = item.designer + ' ' + seasonPretty + ' ' + item.year + (item.itemDetails && item.itemDetails.components ? ' ' + item.itemDetails.components.replace(',', ' &') : '');

  // Use the new thread-based hook
  const { messages, loading, error, sendMessage, markMessagesAsSeen } = useThreadMessages(item?._id, userEmail, currentUserEmail, itemTitle);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setMessageText('');
      setSending(false);
    } else {
      // Mark messages as seen when modal opens
      markMessagesAsSeen();
    }
  }, [isOpen, markMessagesAsSeen]);

  // Scroll to bottom when messages or modal open
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [isOpen, messages]);

  // Like handler for array-of-messages-in-offer structure
  const handleLikeMessage = async (msgIdx) => {
    try {
      if (!item?._id || !userEmail) {
        console.warn('Missing item._id or userEmail', { item, userEmail });
        return;
      }
      const offerDocId = `${item._id}_${userEmail}`;
      const offerRef = doc(db, "offers", offerDocId);
      const offerSnap = await getDoc(offerRef);

      if (!offerSnap.exists()) {
        console.warn('Offer document does not exist:', offerDocId);
        return;
      }

      const offerData = offerSnap.data();
      const messagesArr = offerData.messages || [];
      const msg = messagesArr[msgIdx];

      if (!msg) {
        console.warn('Message not found at index', msgIdx, messagesArr);
        return;
      }

      if (!msg.likes) msg.likes = [];

      const likeIdx = msg.likes.indexOf(userEmail);
      if (likeIdx > -1) {
        // Unlike: remove userEmail from likes
        msg.likes.splice(likeIdx, 1);
      } else {
        // Like: add userEmail to likes
        msg.likes.push(userEmail);
      }

      await updateDoc(offerRef, { messages: messagesArr });
      console.log('Like toggled successfully');
    } catch (err) {
      console.error('Error in handleLikeMessage:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || sending) return;
    setSending(true);
    const success = await sendMessage(messageText, true);
    if (success) {
      setMessageText('');
    }
    setSending(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
        style={{ zIndex: 10 }}
      />
      {/* Drawer */}
      <div
        className={`
          fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl p-6 overflow-y-auto
          transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          pointer-events-auto
        `}
        style={{ zIndex: 20, willChange: 'transform' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="h-full max-h-screen flex justify-between flex-col">
          <div className="top">
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                {item.images && item.images.length > 0 && (
                  <img
                    src={urlFor(item.images[0]).width(800).url()}
                    alt={item.title}
                    className="w-16 h-16 object-cover rounded-md"
                  />
                )}
                <div>
                  <h3 className="font-medium text-lg text-gray-900">{item.designer}</h3>
                  <p className="text-sm text-gray-600">
                    {item.year} {item.season}
                  </p>
                  {item.showPrice && item.price && (
                    <p className="text-sm font-medium text-gray-900">
                      ${item.price.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Messages Thread */}
            <div className="flex-1 mb-4">
              <h4 className="mb-3">Messages</h4>
              {loading ? (
                <div className="text-center py-4 text-gray-500">Loading messages...</div>
              ) : error ? (
                <div className="text-center py-4 text-red-500">Error loading messages</div>
              ) : messages.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No messages yet. Start the conversation!
                </div>
              ) : (
                <div className="space-y-4 max-h-full overflow-y-auto">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`p-4 relative rounded-lg ${msg.user === userEmail ? 'bg-blue-100 ml-12' : 'bg-gray-100 mr-12'}`}
                      onDoubleClick={() => handleLikeMessage(idx)}
                    >
                      <div className="text-sm font-medium text-gray-700 mb-1 relative">
                        {msg.user === userEmail ? 'You' : 'titled works'}
                      </div>
                      <div className="text-sm text-gray-900">{msg.message}</div>
                      <div className={`flex items-start mt-2`}>
                        {msg.timestamp ? (
                          <div className="text-xs text-gray-500 mt-1">
                            {msg.timestamp?.toDate ?
                              msg.timestamp.toDate().toLocaleString() :
                              new Date(msg.timestamp).toLocaleString()
                            }
                          </div>
                        ) : null}
                      </div>

                      {msg.likes && (
                      <button
                          className={`flex items-center absolute bg-white h-6 w-6 flex justify-center items-center rounded-full bottom-[-10px] left-1`}
                          onClick={() => handleLikeMessage(idx)}
                          aria-label="Like message"
                        ><HeartFilled />
                        </button>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </div>
          {/* Message Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1 hidden">
                Message
              </label>
              <textarea
                id="message"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder="Make an offer or ask for additional details…"
                disabled={sending}
              />
            </div>
            <div className="flex space-x-3 pb-6">
              <button
                type="submit"
                className="btn-primary flex-1 disabled:opacity-50"
                disabled={sending || !messageText.trim()}
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Modal; 